import { useEffect, useMemo, useRef, useState } from "react";
import { Wheel } from "react-custom-roulette";
import type { Socket } from "socket.io-client";
import { Gamepad2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { isCustomAppId, type CommonGame } from "@/lib/api";
import { cn } from "@/lib/utils";

const SLICE_COLORS = [
  "#1e3a5f",
  "#0f4c75",
  "#3282b8",
  "#0a2540",
  "#274472",
  "#5c2a9d",
  "#1f6f8b",
  "#2c5364",
];

function trimForWheel(label: string, sliceCount: number): string {
  const max =
    sliceCount > 48 ? 8 : sliceCount > 36 ? 10 : sliceCount > 24 ? 14 : 26;
  return label.length > max ? `${label.slice(0, max - 1)}…` : label;
}

function fontSizeFor(count: number): number {
  if (count <= 12) return 14;
  if (count <= 24) return 11;
  if (count <= 48) return 8;
  if (count <= 96) return 6;
  return 5;
}

const RANDOM_SPIN_MIN_MS = 5500;
const RANDOM_SPIN_MAX_MS = 10_500;
const DEFAULT_SPIN_MS = 7500;
const LIB_BASE_SPIN_MS = 11_350;
const ABS_MIN_SPIN_MS = 600;
const ABS_MAX_SPIN_MS = 13_000;

function randomSpinMs(): number {
  return Math.round(
    RANDOM_SPIN_MIN_MS + Math.random() * (RANDOM_SPIN_MAX_MS - RANDOM_SPIN_MIN_MS),
  );
}

function spinMsToDuration(ms: number): number {
  const clamped = Math.min(ABS_MAX_SPIN_MS, Math.max(ABS_MIN_SPIN_MS, ms));
  return clamped / LIB_BASE_SPIN_MS;
}

interface TournamentSpinningPayload {
  matchNodeId: string;
  initiatorSteamId: string;
  prizeIndex: number;
  durationMs: number;
  startedAt: number;
  slices: CommonGame[];
}

interface TournamentResultPayload {
  matchNodeId: string;
  initiatorSteamId: string;
  appId: number;
  name: string;
}

interface TournamentMatchupWheelProps {
  games: CommonGame[];
  socket: Socket;
  roomCode: string;
  matchNodeId: string | null;
  meSteamId?: string;
  members: { steamId: string; displayName: string }[];
  /** Host for bracket mode; solo player for solo challenge. */
  canSpin: boolean;
  phase: "pick_game" | "awaiting_result" | "complete";
  wheelTitle?: string;
  spinButtonHint?: string;
  onSpinningChange?: (spinning: boolean) => void;
}

export function TournamentMatchupWheel({
  games,
  socket,
  roomCode,
  matchNodeId,
  meSteamId,
  members,
  canSpin,
  phase,
  wheelTitle = "Matchup wheel",
  spinButtonHint,
  onSpinningChange,
}: TournamentMatchupWheelProps) {
  const enabled = games;
  const [override, setOverride] = useState<CommonGame[] | null>(null);
  const [mustSpin, setMustSpin] = useState(false);
  const [prizeIndex, setPrizeIndex] = useState(0);
  const [spinDurationMs, setSpinDurationMs] = useState(DEFAULT_SPIN_MS);
  const [winner, setWinner] = useState<{
    name: string;
    appId: number;
    iconUrl: string | null;
    custom: boolean;
  } | null>(null);
  const [spinningInitiator, setSpinningInitiator] = useState<string | null>(null);
  const [resultPulse, setResultPulse] = useState(false);
  const pulseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (phase === "pick_game" && !mustSpin) {
      setWinner(null);
      setOverride(null);
      setSpinningInitiator(null);
    }
  }, [matchNodeId, phase, mustSpin]);

  const subset = mustSpin && override ? override : enabled;

  const data = useMemo(() => {
    if (subset.length === 0) {
      return [{ option: "—", style: { backgroundColor: "#1e3a5f", textColor: "#f8fafc" } }];
    }
    return subset.map((g, i) => ({
      option: trimForWheel(g.name, subset.length),
      style: {
        backgroundColor: SLICE_COLORS[i % SLICE_COLORS.length]!,
        textColor: "#f8fafc",
      },
    }));
  }, [subset]);

  const wheelKey = useMemo(
    () => `${subset.length}:${subset.map((g) => g.appId).join(",")}`,
    [subset],
  );

  const safePrizeIndex = Math.min(prizeIndex, Math.max(0, data.length - 1));

  const startingOptionIndex = useMemo(() => {
    if (mustSpin || !winner || subset.length === 0) return -1;
    const atSpinIdx =
      prizeIndex >= 0 && prizeIndex < subset.length ? subset[prizeIndex] : undefined;
    if (atSpinIdx && atSpinIdx.appId === winner.appId) return prizeIndex;
    const idx = subset.findIndex((g) => g.appId === winner.appId);
    return idx >= 0 ? idx : 0;
  }, [mustSpin, winner, subset, prizeIndex]);

  const [committedKey, setCommittedKey] = useState<string | null>(null);
  const readyToSpin = mustSpin && committedKey === wheelKey;
  useEffect(() => {
    setCommittedKey(wheelKey);
  }, [wheelKey]);

  useEffect(
    () => () => {
      if (pulseTimer.current) clearTimeout(pulseTimer.current);
    },
    [],
  );

  useEffect(() => {
    onSpinningChange?.(mustSpin);
  }, [mustSpin, onSpinningChange]);

  useEffect(() => {
    function onSpinning(p: TournamentSpinningPayload) {
      if (p.matchNodeId !== matchNodeId) return;
      if (!Array.isArray(p.slices) || p.slices.length === 0) return;
      if (
        typeof p.prizeIndex !== "number" ||
        !Number.isInteger(p.prizeIndex) ||
        p.prizeIndex < 0 ||
        p.prizeIndex >= p.slices.length
      )
        return;
      setOverride(p.slices);
      setPrizeIndex(p.prizeIndex);
      setSpinDurationMs(
        typeof p.durationMs === "number" && Number.isFinite(p.durationMs)
          ? p.durationMs
          : DEFAULT_SPIN_MS,
      );
      setMustSpin(true);
      setWinner(null);
      setSpinningInitiator(p.initiatorSteamId);
      setResultPulse(false);
    }
    function onResult(p: TournamentResultPayload) {
      if (p.matchNodeId !== matchNodeId) return;
      const meta = games.find((g) => g.appId === p.appId);
      setWinner({
        name: p.name,
        appId: p.appId,
        iconUrl: meta?.iconUrl ?? null,
        custom: meta?.custom ?? isCustomAppId(p.appId),
      });
    }
    socket.on("tournament:spinning", onSpinning);
    socket.on("tournament:result", onResult);
    return () => {
      socket.off("tournament:spinning", onSpinning);
      socket.off("tournament:result", onResult);
    };
  }, [socket, games, matchNodeId]);

  function handleSpin() {
    if (!matchNodeId || enabled.length === 0 || mustSpin) return;
    const idx = Math.floor(Math.random() * enabled.length);
    socket.emit("tournament:spin", {
      code: roomCode,
      matchNodeId,
      prizeIndex: idx,
      durationMs: randomSpinMs(),
      slices: enabled.map((g) => ({
        appId: g.appId,
        name: g.name,
        iconUrl: g.iconUrl,
      })),
    });
  }

  function onStopSpinning() {
    setMustSpin(false);
    const game = subset[prizeIndex];
    if (game && spinningInitiator && spinningInitiator === meSteamId && matchNodeId) {
      socket.emit("tournament:result", {
        code: roomCode,
        matchNodeId,
        appId: game.appId,
        name: game.name,
      });
    }
    if (game && !spinningInitiator) {
      setWinner({
        name: game.name,
        appId: game.appId,
        iconUrl: game.iconUrl,
        custom: game.custom ?? isCustomAppId(game.appId),
      });
    }
    setResultPulse(true);
    if (pulseTimer.current) clearTimeout(pulseTimer.current);
    pulseTimer.current = setTimeout(() => setResultPulse(false), 1800);
  }

  const initiatorName = useMemo(() => {
    if (!spinningInitiator) return null;
    return members.find((m) => m.steamId === spinningInitiator)?.displayName ?? "Someone";
  }, [spinningInitiator, members]);

  const canSpinNow =
    canSpin && phase === "pick_game" && matchNodeId != null && enabled.length > 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>{wheelTitle}</span>
          <span className="text-xs font-normal text-muted-foreground">
            {enabled.length} mutual {enabled.length === 1 ? "game" : "games"}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col items-center gap-4">
        {enabled.length === 0 && !mustSpin ? (
          <div className="flex h-72 w-full items-center justify-center rounded-md border border-dashed border-border/60 px-6 text-center text-sm text-muted-foreground">
            No mutual Steam games for this pairing. Sync libraries or adjust disabled games.
          </div>
        ) : (
          <div
            className={cn(
              "transition-transform duration-700 ease-out",
              resultPulse ? "scale-105" : "scale-100",
            )}
          >
            <Wheel
              key={`${wheelKey}:${matchNodeId ?? ""}`}
              mustStartSpinning={readyToSpin}
              prizeNumber={safePrizeIndex}
              data={data}
              startingOptionIndex={startingOptionIndex}
              onStopSpinning={onStopSpinning}
              outerBorderColor="#0b1220"
              outerBorderWidth={6}
              innerRadius={12}
              innerBorderColor="#0b1220"
              radiusLineColor="#0b1220"
              radiusLineWidth={1}
              fontSize={fontSizeFor(subset.length)}
              textDistance={subset.length > 36 ? 54 : 62}
              spinDuration={spinMsToDuration(spinDurationMs)}
            />
          </div>
        )}

        {mustSpin && initiatorName && spinningInitiator !== meSteamId && (
          <p className="text-sm text-muted-foreground">{initiatorName} is spinning…</p>
        )}

        {winner && !mustSpin && phase !== "pick_game" && (
          <div className="flex items-center gap-2 rounded-md border border-primary/30 bg-primary/10 px-3 py-2 text-primary">
            {winner.iconUrl ? (
              <img
                src={winner.iconUrl}
                alt=""
                className="h-5 w-5 shrink-0 rounded-sm object-cover"
              />
            ) : (
              <Gamepad2 className="h-4 w-4 shrink-0 text-muted-foreground" />
            )}
            <span className="font-medium">{winner.name}</span>
          </div>
        )}

        <Button
          className="w-full"
          size="lg"
          disabled={!canSpinNow || mustSpin}
          onClick={handleSpin}
        >
          {mustSpin
            ? "Spinning…"
            : spinButtonHint ??
              (canSpin ? "Spin for matchup" : "Only the host can spin for this matchup")}
        </Button>
      </CardContent>
    </Card>
  );
}
